import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Search, Loader2, MapPin } from "lucide-react";

export default function GooglePlaceSearch({ onSelect, placeholder = "Search for a business on Google…", big }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const coords = useRef(null);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { coords.current = { lat: p.coords.latitude, lng: p.coords.longitude }; },
        () => {}, { timeout: 4000 }
      );
    }
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = useCallback((text) => {
    if (timer.current) clearTimeout(timer.current);
    if (!text || text.trim().length < 3) { setSuggestions([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const params = { q: text.trim() };
        if (coords.current) { params.lat = coords.current.lat; params.lng = coords.current.lng; }
        const { data } = await api.get("/places/autocomplete", { params });
        setSuggestions(data);
        setOpen(true);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 500);
  }, []);

  const pick = async (s) => {
    setFetching(true);
    setQ(s.main_text || s.text);
    setOpen(false);
    try {
      const { data } = await api.get(`/places/details/${s.place_id}`);
      onSelect(data);
    } catch {}
    finally { setFetching(false); }
  };

  const h = big ? "h-14 text-base" : "h-11";
  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <input
          value={q}
          data-testid="google-search-input"
          onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder={placeholder}
          className={`w-full ${h} pl-10 pr-10 rounded-md border border-[#D1D5DB] bg-white focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]`}
        />
        {(loading || fetching) && <Loader2 className="w-5 h-5 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#1E3A8A]" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-[1200] mt-1 w-full bg-white border border-[#E5E7EB] rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="google-suggestions">
          {suggestions.map((s) => (
            <button key={s.place_id} type="button" onClick={() => pick(s)} data-testid={`suggestion-${s.place_id}`}
              className="w-full text-left px-3 py-2.5 hover:bg-[#F9FAFB] border-b border-[#F3F4F6] last:border-0 flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#1E3A8A] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-bold text-[#111827] text-sm truncate">{s.main_text || s.text}</div>
                {s.secondary_text && <div className="text-xs text-[#6B7280] truncate">{s.secondary_text}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
